import { get, put, del, post } from '@/lib/api/client'
import {
  type ApiResponse,
  PaginatedWebPageResponse,
  PaginatedWebPageResponseSchema,
  WebPagesStatsResponse,
  WebPagesStatsResponseSchema,
  WebsitePageVersionsResponse,
  WebsitePageVersionsResponseSchema,
  WebsitePageContentResponse,
  WebsitePageContentResponseSchema,
  WebPageVersionsStatsResponse,
  WebPageVersionsStatsResponseSchema,
  PaginationParams,
  PaginationParamsSchema
} from '@/lib/api/types'
import { WebsitePageSection } from '@/types';

export class WebPagesService {
  /**
   * Get web pages with pagination and filtering
   */
  async getPaginatedWebPages(params: PaginationParams = { page: 1, size: 20 }): Promise<PaginatedWebPageResponse> {
    const validatedParams = PaginationParamsSchema.parse(params);
    const query = new URLSearchParams({
      page: validatedParams.page.toString(),
      size: validatedParams.size.toString()
    }).toString();
    const res = await get(`website-pages/private/all?${query}`);
    return PaginatedWebPageResponseSchema.parse(res);
  }

  async getWebPagesStats(): Promise<WebPagesStatsResponse> {
    // TODO: Confirm and update this endpoint path with backend contract.
    const res = await get('website-pages/private/stats');
    return WebPagesStatsResponseSchema.parse(res);
  }

  async getWebPageVersions(id: string, params: PaginationParams = { page: 1, size: 20 }): Promise<WebsitePageVersionsResponse> {
    const validatedParams = PaginationParamsSchema.parse(params);
    const query = new URLSearchParams({
      page: validatedParams.page.toString(),
      size: validatedParams.size.toString()
    }).toString();
    const res = await get(`website-pages/private/${id}/versions?${query}`);

    return WebsitePageVersionsResponseSchema.parse(res);
  }

  async getWebPageContent(contentId: string): Promise<WebsitePageContentResponse> {
    const res = await get(`website-pages/private/content/${contentId}`);
    return WebsitePageContentResponseSchema.parse(res);
  }
  async getWebPageVersionStats(id: string): Promise<WebPageVersionsStatsResponse> {
    const res = await get(`website-pages/private/${id}/content/stats`)
    return WebPageVersionsStatsResponseSchema.parse(res)
  }

  /**
   * Update all web page sections at once
   */
  async updateAllWebPageSections(
    contentId: string,
    data: { status: 'DRAFT' | 'PUBLISH'; contents: WebsitePageSection[] }
  ): Promise<ApiResponse<Record<string, unknown>>> {
    return await put<ApiResponse<Record<string, unknown>>>(
      `website-pages/admin/private/content/${contentId}`,
      data
    )
  }

  /**
   * Update web page status
   */
  async updateWebPageStatus(
    contentId: string,
    status: 'DRAFT' | 'PUBLISH'
  ): Promise<ApiResponse<Record<string, unknown>>> {
    return await put<ApiResponse<Record<string, unknown>>>(
      `website-pages/admin/private/content/${contentId}/status`,
      { status }
    )
  }

  /**
   * Delete a web page content version
   */
  async deleteWebPageContent(contentId: string): Promise<ApiResponse<Record<string, unknown>>> {
    return await del<ApiResponse<Record<string, unknown>>>(
      `website-pages/admin/private/content/${contentId}`
    )
  }

  /**
   * Create a new web page content version
   */
  async createWebPageVersion(
    websitePageId: string,
    data: { status: 'DRAFT' | 'PUBLISH'; contents: WebsitePageSection[] }
  ): Promise<ApiResponse<Record<string, unknown>>> {
    return await post<ApiResponse<Record<string, unknown>>>(
      `website-pages/admin/private/${websitePageId}/content`,
      data
    )
  }
}

// Export singleton instance
export const webPageService = new WebPagesService()

// Export default
export default webPageService